import { useEffect, useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiError } from '@bumble/app/utils/api-error';
import { toast } from 'react-toastify';
import { Loading } from '@bumble/app/components/Loading';

interface UsbDeviceEditProps {
  onSubmit: () => void;
  cpeID: string,
  hwIDPath: string,
}

export const UsbDeviceEdit = ({
  onSubmit, cpeID, hwIDPath, 
}: UsbDeviceEditProps) => {
  const api = useApi();

  const parityData = ['none', 'even', 'odd' ];

  const baudRateData = [
    50, 75, 110, 134, 150, 200, 300, 600, 1200, 1800, 2400, 
    4800, 9600, 19200, 38400, 57600, 115200, 230400, 460800, 921600,
  ];

  const startBitsData = [7, 8];

  const stopBitsData = [1, 2];

  const [newPortLabel, setNewPortLabel] = useState<string>('');
  const [newPortDescription, setNewPortDescription] = useState<string>('');
  const [baudRate, setBaudRate] = useState<number[]>(baudRateData);
  const [selectedBaudRate, setSelectedBaudRate] = useState<number>(baudRate[16])
  const [parity, setParity] = useState<string[]>(parityData);
  const [selectedParity, setSelectedParity] = useState(parity[0]);
  const [startBits, setStartBits] = useState<number[]>(startBitsData);
  const [selectedStartBit, setSelectedStartBit] = useState<number>(startBits[1]);
  const [stopBits, setStopoBits] = useState<number[]>(stopBitsData);
  const [selectedStopBit, setSelectedStopBit] = useState<number>(stopBits[0]);

  const { isLoading, data: usbDeviceData } = useQuery(
    ['usbDeviceData'],
    api.getUsbConsoleSettingsEndpointApiV1GetUsbConsoleSettingsGet.bind(null, {
      cpe_id: cpeID,
      hw_id_path: hwIDPath,
    }),
    { enabled: true, cacheTime: 0, initialData: undefined, retry: false },
  );

  useEffect(() => {
    if (!usbDeviceData) return;
    setNewPortLabel(usbDeviceData.data.label ?? "");
    setNewPortDescription(usbDeviceData.data.description ?? "");
    setSelectedBaudRate(usbDeviceData.data.baud_rate ?? 115200);
    setSelectedParity(usbDeviceData.data.parity ?? "none");
    setSelectedStartBit(usbDeviceData.data.bits ?? 8);
    setSelectedStopBit(usbDeviceData.data.stop_bits ?? 1);
  }, [usbDeviceData]);

  const saveUsbSettings = useMutation(api.saveUsbConsoleSettingsEndpointApiV1SaveUsbConsoleSettingsPost, {
    onSuccess() {
      toast.success('Save the changes successfully.');
      onSubmit();
    },
    onError: apiError
  })

  const handleSubmit = () => {
    saveUsbSettings.mutate({
      cpe_id: cpeID,  
      hw_id_path: hwIDPath,
      label: newPortLabel,
      description: newPortDescription,
      baud_rate: selectedBaudRate,
      parity: selectedParity,
      bits: selectedStartBit,
      stop_bits: selectedStopBit,
    })
  }

  if (isLoading) return <Loading />

  return (
    <Box pt={2}>
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Port Label"
        variant="outlined"
        value={newPortLabel}
        onChange={(e) => setNewPortLabel(e.target.value)}
        autoFocus
      />
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Description"
        variant="outlined"
        value={newPortDescription}
        onChange={(e) => setNewPortDescription(e.target.value)}
      />
      <Box display="flex" sx={{ mb: 4, gap: 2 }}>
        <FormControl fullWidth>
          <InputLabel id='baudrate'>Baud Rate</InputLabel>
          <Select
            labelId="baudrate"
            value={selectedBaudRate.toString()}
            label="Baud Rate"
            onChange={({ target: { value }}: SelectChangeEvent) => {
              setSelectedBaudRate(parseInt(value));
            }}
            fullWidth
          >
            { baudRate && baudRate.map((item, i) => {
              return <MenuItem value={item} key={item}>{item}</MenuItem>;
            })}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id='parity'>Parity</InputLabel>
          <Select
            labelId="parity"
            value={selectedParity}
            label="Parity"
            onChange={({ target: { value }}: SelectChangeEvent) => {
              setSelectedParity(value);
            }}
            fullWidth
          >
            { parityData && parityData.map((item, i) => {
              return <MenuItem value={item} key={item}>{item}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </Box>
      <Box display="flex" sx={{ mb: 4, gap: 2 }}>
        <FormControl fullWidth>
          <InputLabel id='startbit'>Bits</InputLabel>
          <Select
            labelId="startbit"
            value={selectedStartBit.toString()}
            label="Bits"
            onChange={({ target: { value }}: SelectChangeEvent) => {
              setSelectedStartBit(parseInt(value));
            }}
            fullWidth
          >
            { startBitsData && startBitsData.map((item, i) => {
              return <MenuItem value={item} key={item}>{item}</MenuItem>;
            })}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id='stopbit'>Stop Bits</InputLabel>
          <Select
            labelId="stopbit"
            value={selectedStopBit.toString()}
            label="Stop Bits"
            onChange={({ target: { value }}: SelectChangeEvent) => {
              setSelectedStopBit(parseInt(value));
            }}
            fullWidth
          >
            { stopBitsData && stopBitsData.map((item, i) => {
              return <MenuItem value={item} key={item}>{item}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </Box>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="outlined" onClick={handleSubmit}>
          Save
        </Button>
      </Box>
    </Box>
  );
};
